import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { createPortal } from "react-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { usePlatformAPI } from "@/api/PlatformProvider";
import { PartnerAppForm, type PartnerAppFormValues } from "./PartnerAppForm";

type CreateResponse = {
    portalCode: string;
};

function PortalCodeModal({ code, onClose }: { code: string; onClose: () => void }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                <div className="px-6 pt-6 pb-4">
                    <h2 className="text-base font-semibold text-dark-dark-grey">App created — save the portal code</h2>
                    <p className="mt-2 text-sm text-grey leading-relaxed">
                        This 6-digit code is shown once and cannot be recovered. Share it with the partner via a secure out-of-band channel. They will need it to claim credentials.
                    </p>
                </div>
                <div className="mx-6 mb-6 flex items-center gap-3 bg-glacier-grey rounded-lg px-4 py-3 border border-smokey-purple/30">
                    <span className="flex-1 text-3xl font-mono font-semibold tracking-[0.3em] text-titanium-violet">{code}</span>
                    <button
                        onClick={handleCopy}
                        className="text-titanium-violet/60 hover:text-titanium-violet transition-colors"
                        title="Copy code"
                    >
                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </button>
                </div>
                <div className="flex justify-end px-6 py-4 bg-desaturated-grey border-t border-light-light-grey">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-blue text-white hover:bg-blue/90 transition-colors"
                    >
                        I&apos;ve saved it
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

function CreatePartnerAppPage() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const api = usePlatformAPI();
    const appsFrom = encodeURIComponent(searchParams.get("from") ?? "/partners");
    const partnerName = searchParams.get("partnerName");
    const partnerNameParam = partnerName ? `&partnerName=${encodeURIComponent(partnerName)}` : "";
    const appsURL = `/partners/${id}?from=${appsFrom}${partnerNameParam}`;
    const [portalCode, setPortalCode] = useState<string | null>(null);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-row items-center gap-4">
                <button onClick={() => navigate(appsURL)} className="text-grey hover:text-dark-dark-grey flex items-center gap-1">
                    <ArrowBackIcon fontSize="small" />
                </button>
                <h1 className="flex items-center gap-2 text-xl">
                    <span className="text-grey font-medium">Apps</span>
                    <span className="text-light-grey">/</span>
                    <span className="font-semibold">Create App</span>
                </h1>
            </div>
            <PartnerAppForm
                submitLabel="Create App"
                onSubmit={async (values: PartnerAppFormValues) => {
                    const [data, err] = await api.post<CreateResponse>(`api/partners/${id}/apps`, {
                        name: values.name,
                        description: values.description || null,
                        notificationEmail: values.notificationEmail,
                        scope: JSON.parse(values.scope || "{}"),
                        requireSigning: values.requireSigning,
                        rollAfterDays: parseInt(values.rollAfterDays, 10) || null,
                        rolloverDays: parseInt(values.rolloverDays, 10) || null,
                        codeLength: parseInt(values.codeLength, 10) || 8,
                        codeFormat: values.codeFormat,
                    });
                    if (err) throw err;
                    setPortalCode(data.portalCode);
                }}
            />
            {portalCode && (
                <PortalCodeModal
                    code={portalCode}
                    onClose={() => navigate(appsURL)}
                />
            )}
        </div>
    );
}

export default CreatePartnerAppPage;
